import { MatDialog } from '@angular/material/dialog';
import {
  Component,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  Host,
  Inject,
  Optional,
  Input,
} from '@angular/core';
import {
  QuickBuildStorageService,
  StoredQuickBuild,
} from 'src/app/core/services/quick-build-storage.service';
import { StoredItemsBrowserComponent } from '../components/stored-items-browser/stored-items-browser.component';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Log } from 'src/app/logger.service';
import { DarkModeService } from '../dark-mode/dark-mode.service';
import { MAT_MENU_PANEL, MatMenuPanel } from '@angular/material/menu';

@Component({
    selector: 'app-quickbuild-dropdown',
    templateUrl: './quickbuild-dropdown.component.html',
    styleUrls: ['./quickbuild-dropdown.component.scss'],
    standalone: false
})
export class QuickBuildDropdownComponent implements OnInit, OnDestroy {
  quickBuilds: StoredQuickBuild[] = [];
  recentlyUsedQuickBuilds: StoredQuickBuild[] = [];
  popularQuickBuilds: StoredQuickBuild[] = [];
  popularQuickBuildsFirstColumn: StoredQuickBuild[] = [];
  popularQuickBuildsSecondColumn: StoredQuickBuild[] = [];
  private readonly RECENT_QUICKBUILDS_KEY = 'recentlyUsedQuickBuilds';
  private readonly MAX_RECENT_QUICKBUILDS = 10;
  private quickBuildsSubscription?: Subscription;
  quickBuildsLoaded = false; // Used in template for loading states

  @Input() embedded = false; // Removes header
  @Output() quickBuildSelected = new EventEmitter<StoredQuickBuild>();

  constructor(
    private quickBuildStorageService: QuickBuildStorageService,
    private dialog: MatDialog,
    public darkModeService: DarkModeService,
    @Optional() @Host() @Inject(MAT_MENU_PANEL) private matMenuPanel?: MatMenuPanel<any>
  ) {}

  ngOnInit() {
    // Always load quickbuilds on init to avoid delay when menu opens
    this.loadQuickBuilds();
  }

  private loadQuickBuilds() {
    if (!this.quickBuildsSubscription) {
      // Subscribe to quickbuilds - BehaviorSubject will emit cached value immediately if available
      this.quickBuildsSubscription = this.quickBuildStorageService.items$
        .pipe(filter((quickBuilds) => quickBuilds !== null))
        .subscribe((quickBuilds) => {
          if (quickBuilds) {
            this.processQuickBuilds(quickBuilds);
          }
        });
    }
  }

  private processQuickBuilds(quickBuilds: StoredQuickBuild[]) {
    this.quickBuilds = quickBuilds.sort((a, b) => a.name.localeCompare(b.name));
    this.quickBuildsLoaded = true;

    // Get recently used quickbuilds from localStorage first
    this.loadRecentQuickBuilds();

    // Get popular quickbuilds (public scope with "Popular" category)
    // Filter out any that are already shown in recent
    const recentIds = new Set(this.recentlyUsedQuickBuilds.map((qb) => qb.id));
    const allPopularQuickBuilds = quickBuilds.filter(
      (qb) => qb.scope === 'public' && qb.category === 'Popular' && !recentIds.has(qb.id)
    );

    // Store first 4 for when showing alongside recent items
    this.popularQuickBuilds = allPopularQuickBuilds.slice(0, 4);

    // For two-column layout when no recent items, take up to 8 items
    const popularForColumns = allPopularQuickBuilds.slice(0, 8);
    const midPoint = Math.ceil(popularForColumns.length / 2);
    this.popularQuickBuildsFirstColumn = popularForColumns.slice(0, midPoint);
    this.popularQuickBuildsSecondColumn = popularForColumns.slice(midPoint);
  }

  ngOnDestroy() {
    if (this.quickBuildsSubscription) {
      this.quickBuildsSubscription.unsubscribe();
    }
  }

  private loadRecentQuickBuilds() {
    const storedIds = this.getRecentQuickBuildIds();
    if (storedIds.length > 0) {
      this.recentlyUsedQuickBuilds = storedIds
        .map((id) => this.quickBuilds.find((qb) => qb.id === id))
        .filter((quickBuild): quickBuild is StoredQuickBuild => quickBuild !== undefined)
        .slice(0, 4);
    } else {
      this.recentlyUsedQuickBuilds = [];
    }
  }

  private updatePopularQuickBuilds() {
    // Re-filter popular quickbuilds to exclude items now in recent
    const recentIds = new Set(this.recentlyUsedQuickBuilds.map((qb) => qb.id));
    const allPopularQuickBuilds = this.quickBuilds.filter(
      (qb) => qb.scope === 'public' && qb.category === 'Popular' && !recentIds.has(qb.id)
    );

    // Store first 4 for when showing alongside recent items
    this.popularQuickBuilds = allPopularQuickBuilds.slice(0, 4);

    // For two-column layout when no recent items, take up to 8 items
    const popularForColumns = allPopularQuickBuilds.slice(0, 8);
    const midPoint = Math.ceil(popularForColumns.length / 2);
    this.popularQuickBuildsFirstColumn = popularForColumns.slice(0, midPoint);
    this.popularQuickBuildsSecondColumn = popularForColumns.slice(midPoint);
  }

  private getRecentQuickBuildIds(): string[] {
    try {
      const stored = localStorage.getItem(this.RECENT_QUICKBUILDS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveRecentQuickBuild(quickBuildId: string) {
    let recentIds = this.getRecentQuickBuildIds();
    // Remove if already exists and add to front
    recentIds = recentIds.filter((id) => id !== quickBuildId);
    recentIds.unshift(quickBuildId);
    // Keep only the most recent MAX_RECENT_QUICKBUILDS
    recentIds = recentIds.slice(0, this.MAX_RECENT_QUICKBUILDS);

    try {
      localStorage.setItem(this.RECENT_QUICKBUILDS_KEY, JSON.stringify(recentIds));
    } catch (e) {
      Log.s('Failed to save recent quickbuilds:', 'error', {});
    }

    // Reload recent quickbuilds and update popular list to exclude recent items
    this.loadRecentQuickBuilds();
    this.updatePopularQuickBuilds();
  }

  onSelectionChange(quickBuildId: string | null) {
    let quickBuild = this.quickBuilds.find((qb) => qb.id === quickBuildId);

    if (quickBuildId && quickBuild) {
      this.saveRecentQuickBuild(quickBuildId);
      quickBuild = QuickBuildStorageService.copyStoredQuickBuild(quickBuild);
    }

    this.quickBuildSelected.emit(quickBuild);
  }

  trackByQuickBuildId(index: number, quickBuild: StoredQuickBuild): string {
    return quickBuild.id;
  }

  onViewAll() {
    this.matMenuPanel?.close?.emit();

    const dialogRef = this.dialog.open(StoredItemsBrowserComponent, {
      width: '95vw',
      height: '90vh',
      maxWidth: '1200px',
      maxHeight: '1000px',
      panelClass: 'saved-lists-dialog',
      backdropClass: 'tw-bg-black/80',
      data: {
        items$: this.quickBuildStorageService.items$,
        config: this.quickBuildStorageService.getBrowserConfig(),
      },
    });

    dialogRef.afterClosed().subscribe((result: StoredQuickBuild | undefined) => {
      if (result) {
        this.onSelectionChange(result.id);
      }
    });
  }
}
